/* eslint-disable @typescript-eslint/no-unused-vars */
import { Flex, Table } from 'antd';
import { ColumnsType, TableProps } from 'antd/es/table';
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Typography } from 'tfc-components';

import SearchBar from '../SearchBar';

import Button from 'components/atoms/Button';
import Icon from 'components/atoms/Icon';
import { TCleanRequire } from 'pages/ChangePlan';
import { StorageAgentDataType } from 'services/agents/types';
import { getAgentDataSourceByCodeService } from 'services/agentsDataSource';
import { AgentDataSourceType } from 'services/agentsDataSource/types';
import mapModifiers, { formatBytes, handleIconType, handleType } from 'utils/functions';

interface Props {
  dataAgent: StorageAgentDataType[];
  sourceType: number;
  setCleanRequire: (data: TCleanRequire) => void;
  cleanRequire: TCleanRequire;
  disabledAgents: string[]
}
const FileInChatbot: React.FC<Props> = ({
  dataAgent, sourceType, setCleanRequire, cleanRequire, disabledAgents
}) => {
  /* States */
  const [agentSelected, setAgentSelected] = useState<string>('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [searchTxt, setSearchTxt] = useState('');

  const isDisabled = useMemo(
    () => disabledAgents?.includes(agentSelected),
    [agentSelected, disabledAgents]
  );

  /* Queries */
  const { data: dataFile } = useQuery(
    ['get-agent-data-source-by-code', agentSelected, sourceType],
    () => getAgentDataSourceByCodeService(agentSelected, {
      sourceType: `${sourceType}`,
      page: 1,
      limit: 100,
    }),
    {
      enabled: !!agentSelected,

    },
  );

  const renderChatbot = (item: StorageAgentDataType, index: number) => (
    <div key={`chatbot${index}`}>
      <Button onClick={() => setAgentSelected(item?.code)} className={mapModifiers('m-fileInChatbot_btnAgent', item?.code === agentSelected ? 'active' : '')}>
        {item?.name}
      </Button>
    </div>
  );

  const columns: ColumnsType<AgentDataSourceType> = [
    {
      title: (
        <Typography.Text className="t-accountList_table-head">
          Name
        </Typography.Text>
      ),
      dataIndex: 'name',
      key: 'agentDocumentData.name',
      render: (_name: string, data: AgentDataSourceType) => (
        <Typography.Text>
          {data.agentDocumentData.name}
        </Typography.Text>
      ),
      width: sourceType === 1 ? '50%' : '33%'
    },
    ...(sourceType === 2 ? [{
      title: <Typography.Text className="t-accountList_table-head">Type</Typography.Text>,
      dataIndex: 'sourceType',
      key: 'sourceType',
      width: '33%',

      render: (_name: string, data: AgentDataSourceType) => (
        <div
          style={{
            wordWrap: 'break-word',
            wordBreak: 'break-word',
            minWidth: 40,
            display: 'flex',
            alignItems: 'center',
          }}
          className="t-knowledge_tableContent"
        >
          <div className="t-knowledge_statusWrap">
            <Icon iconName={handleIconType(data.agentDocumentData.sourceType)} size="20" style={{ marginRight: 8 }} />
            <Typography.Text>{handleType(data.agentDocumentData.sourceType)}</Typography.Text>
          </div>
        </div>
      ),
    }] : []),

    ...sourceType === 2 ? [{
      title: <Typography.Text className="t-accountList_table-head">Size</Typography.Text>,
      dataIndex: 'size',
      key: 'size',
      width: '33%',
      render: (_name: string, data: AgentDataSourceType) => (

        <Typography.Text>
          {
            formatBytes(parseInt(data.agentDocumentData.size, 10))
          }
        </Typography.Text>
      ),
    }] : [],
    ...sourceType === 1 ? [{
      title: <Typography.Text className="t-accountList_table-head">URL</Typography.Text>,
      dataIndex: 'size',
      key: 'size',
      width: '50%',
      render: (_name: string, data: AgentDataSourceType) => (
        <div
          style={{
            wordWrap: 'break-word',
            wordBreak: 'break-word',
            minWidth: 40,
            display: 'flex',
            alignItems: 'center',
          }}
          className="t-knowledge_tableContent"
        >
          <div className="">
            <Typography.Text>
              {
                data?.agentDocumentData?.name
              }
            </Typography.Text>
          </div>
        </div>
      ),
    }] : [],

  ];

  const components = {
    header: {
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: (props: any) => (
        <th
          {...props}
          style={{
            backgroundColor: 'white',
            fontWeight: 'bold',
          }}
        />
      ),
    },
  };

  const rowSelection: TableProps<AgentDataSourceType>['rowSelection'] = {
    selectedRowKeys,
    onChange: isDisabled ? undefined : (keys, rows) => {
      setSelectedRowKeys(keys as string[]);

      if (!agentSelected) return;
      if (sourceType === 2) {
        const total = rows.reduce((acc, item) => acc + (item.agentDocumentData.sizeMb || 0), 0);
        setCleanRequire({
          ...cleanRequire,
          storage: {
            ...cleanRequire.storage,
            cleaned: {
              ...cleanRequire.storage.cleaned,
              [agentSelected]: total
            },
            code: {
              ...cleanRequire.storage.code,
              [agentSelected]: keys as string[]
            },
          },
          file: {
            ...cleanRequire.file,
            cleaned: {
              ...cleanRequire.file.cleaned,
              [agentSelected]: keys.length
            },
            code: {
              ...cleanRequire.file.code,
              [agentSelected]: keys as string[]
            },
          }
        });
      } else {
        setCleanRequire({
          ...cleanRequire,
          web: {
            ...cleanRequire.web,
            cleaned: {
              ...cleanRequire.web.cleaned,
              [agentSelected]: rows.length
            },
            code: {
              ...cleanRequire.web.code,
              [agentSelected]: keys as string[]
            },
          }
        });
      }
    },
    getCheckboxProps: () => ({
      disabled: isDisabled,
      style: isDisabled ? { display: 'none' } : undefined // 👈 ẨN CHECKBOX
    }),
  };

  useEffect(() => {
    if (!agentSelected) return;
    if (sourceType === 1) {
      setSelectedRowKeys(cleanRequire.web.code?.[agentSelected] || []);
    } else {
      setSelectedRowKeys(cleanRequire.file.code?.[agentSelected] || []);
    }
  }, [agentSelected, cleanRequire.file.code, cleanRequire.web.code, sourceType]);

  const dataTable = useMemo(() => {
    let data;
    if (searchTxt) {
      data = dataFile?.data.filter(
        (item) => item.agentDocumentData.name?.toLocaleLowerCase()
          .includes(searchTxt.toLocaleLowerCase())
      );
    } else {
      data = dataFile?.data;
    }
    return data;
  }, [dataFile?.data, searchTxt]);

  useEffect(() => {
    if (dataAgent.length > 0) setAgentSelected(dataAgent[0]?.code);
  }, [dataAgent]);

  useEffect(() => {
    if (isDisabled) {
      setSelectedRowKeys([]);
      setCleanRequire({
        ...cleanRequire,
        file: {
          ...cleanRequire.file,
          code: {
            ...cleanRequire.file.code,
            [agentSelected]: []
          },
          cleaned: {
            ...cleanRequire.file.cleaned,
            [agentSelected]: 0
          }
        },
        web: {
          ...cleanRequire.web,
          code: {
            ...cleanRequire.web.code,
            [agentSelected]: []
          },
          cleaned: {
            ...cleanRequire.web.cleaned,
            [agentSelected]: 0
          }
        }
      });
    }
  }, [agentSelected, isDisabled]);

  return (
    <Flex className="m-fileInChatbot">
      <Flex vertical gap={12} className="m-fileInChatbot_listChatbot">
        {
          dataAgent?.map(renderChatbot)
        }
      </Flex>

      <Flex className="m-fileInChatbot_listFile" flex={1} vertical>
        <SearchBar onChange={(txt) => setSearchTxt(txt)} />
        <Table
          dataSource={dataTable}
          rowKey={(record) => String(record.agentDocumentData.code)}
          columns={columns}
          rowSelection={rowSelection}
          components={components}
        />
      </Flex>
    </Flex>
  );
};

export default FileInChatbot;
